import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { ChevronDownIcon, ChevronUpIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { Checkbox, SelectInput, TextareaInput } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { getPlanSymbols, savePlanSymbols, type FieldTemplate } from "@/lib/wartungen";
import type { BuilderField } from "./fieldModel";
import { PlanSymbolPreview } from "./PlanSymbolPreview";
import { RuleValueInput } from "./RuleValueInput";
import {
  BEHAVIOR_OPERATOR_LABELS,
  coerceRuleValueForOperator,
  isBooleanSourceField,
  isOptionSourceField,
  normalizeOperator,
  operatorChoicesForSourceField,
} from "./behaviorRules";
import {
  PLAN_LABEL_BUILTIN_SOURCES,
  extractPlaceholderTokens,
  formatPlanVisibilitySentence,
  normalizePlaceholderBindings,
  normalizePlanSymbolRulesForSave,
  planBooleanSelection,
  planRuleClauseText,
  planSymbolSourceFields,
  rasterizeSvgToPng,
  stripDataUrlPrefix,
  type PlanRuleJoin,
  type PlanSourceField,
  type PlaceholderBinding,
  type SymbolRule,
} from "./planSymbolUtils";
import "./planSymbols.css";

interface LocalSymbol {
  localKey: string;
  serverId: number | null;
  title: string;
  svg_markup: string;
  png_preview_base64: string;
  rules: SymbolRule[];
  placeholderBindings: PlaceholderBinding[];
}

function buildLocal(raw: Record<string, unknown>): LocalSymbol {
  const id = Number(raw.id);
  const behavior = (raw.visibility_behavior_json as { rules?: SymbolRule[] } | undefined) || {};
  return {
    localKey: Number.isFinite(id) ? `srv-${id}` : `new-${crypto.randomUUID?.() || Math.random()}`,
    serverId: Number.isFinite(id) ? id : null,
    title: raw.title != null ? String(raw.title) : "",
    svg_markup: String(raw.svg_markup || ""),
    png_preview_base64: String(raw.png_preview_base64 || raw.png_base64 || ""),
    rules: Array.isArray(behavior.rules) ? behavior.rules : [],
    placeholderBindings: normalizePlaceholderBindings(raw.svg_placeholder_bindings_json),
  };
}

function snapshotSymbols(symbols: LocalSymbol[]) {
  return JSON.stringify(
    symbols.map(({ localKey: _k, ...rest }) => ({
      ...rest,
      placeholderBindings: rest.placeholderBindings.filter((b) => b.placeholder || b.source_field_key),
    })),
  );
}

export function PlanSymbolsSection({
  categoryKey,
  masterdataFields,
  templates = [],
}: {
  categoryKey: string;
  masterdataFields: BuilderField[];
  templates?: FieldTemplate[];
}) {
  const [symbols, setSymbols] = useState<LocalSymbol[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savedSignature = useRef<string>("");

  const load = useCallback(async () => {
    if (!categoryKey) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await getPlanSymbols(categoryKey);
      const local = list.map((s) => buildLocal(s as Record<string, unknown>));
      setSymbols(local);
      savedSignature.current = snapshotSymbols(local);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Plansymbole konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [categoryKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(() => snapshotSymbols(symbols) !== savedSignature.current, [symbols]);

  const sourceFields = useMemo(
    () => planSymbolSourceFields(masterdataFields, templates),
    [masterdataFields, templates],
  );

  const update = useCallback((localKey: string, patch: Partial<LocalSymbol>) => {
    setSymbols((prev) => prev.map((s) => (s.localKey === localKey ? { ...s, ...patch } : s)));
  }, []);

  function addSymbol() {
    setSymbols((prev) => [
      ...prev,
      {
        localKey: `new-${crypto.randomUUID?.() || Math.random()}`,
        serverId: null,
        title: "",
        svg_markup: "",
        png_preview_base64: "",
        rules: [],
        placeholderBindings: [],
      },
    ]);
  }

  function move(localKey: string, dir: -1 | 1) {
    setSymbols((prev) => {
      const idx = prev.findIndex((s) => s.localKey === localKey);
      const to = idx + dir;
      if (idx < 0 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [m] = next.splice(idx, 1);
      if (!m) return prev;
      next.splice(to, 0, m);
      return next;
    });
  }

  function remove(localKey: string) {
    setSymbols((prev) => prev.filter((s) => s.localKey !== localKey));
  }

  async function save() {
    setSaving(true);
    try {
      const payload = await Promise.all(
        symbols.map(async (sym, index) => {
          let png = stripDataUrlPrefix(sym.png_preview_base64);
          if (!png && sym.svg_markup.trim()) {
            png = stripDataUrlPrefix(await rasterizeSvgToPng(sym.svg_markup));
          }
          const hasAlways = sym.rules.some((r) => r.rule_type === "alwaysVisible");
          const bindings = sym.placeholderBindings.filter((b) => b.placeholder && b.source_field_key);
          return {
            sort_order: index,
            title: sym.title.trim() || null,
            svg_markup: sym.svg_markup.trim(),
            png_base64: png,
            visibility_behavior_json: {
              rules: hasAlways
                ? [{ rule_type: "alwaysVisible" }]
                : normalizePlanSymbolRulesForSave(sym.rules, sourceFields),
            },
            svg_placeholder_bindings_json: bindings,
          };
        }),
      );
      await savePlanSymbols(categoryKey, payload);
      toast.success("Plansymbole gespeichert.");
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Plansymbole konnten nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mb-builder-card rounded-ios border border-separator bg-card shadow-ios">
      <header className="mb-builder-card__header flex flex-wrap items-start justify-between gap-3 border-b border-separator px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-label">Plansymbole</h2>
          <p className="mt-1 text-sm text-label-secondary">
            SVG-Symbole für Planlabels mit Live-Vorschau, Sichtbarkeitsregeln und Platzhalter-Zuordnung.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="secondary" onClick={addSymbol}>
            Symbol hinzufügen
          </Button>
          <Button type="button" size="sm" loading={saving} disabled={!dirty} onClick={() => void save()}>
            Plansymbole speichern
          </Button>
        </div>
      </header>

      <div className="px-5 py-4">
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner /> Lade Plansymbole …
          </p>
        ) : symbols.length === 0 ? (
          <div className="mb-ps-empty">
            <span className="mb-ps-empty__icon" aria-hidden>
              ◇
            </span>
            <div>
              <p className="text-sm font-medium text-label">Noch keine Plansymbole</p>
              <p className="mt-1 max-w-md text-sm text-label-secondary">
                Lege SVG-Symbole für diese Wartungskategorie an. Die Vorschau aktualisiert sich beim
                Bearbeiten des Markups in Echtzeit.
              </p>
            </div>
            <Button type="button" size="sm" onClick={addSymbol}>
              Erstes Symbol anlegen
            </Button>
          </div>
        ) : (
          <div className="mb-ps-list">
            {symbols.map((sym, idx) => (
              <SymbolEditor
                key={sym.localKey}
                sym={sym}
                index={idx}
                count={symbols.length}
                masterdataFields={masterdataFields}
                sourceFields={sourceFields}
                onUpdate={update}
                onMove={move}
                onRemove={remove}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function SymbolEditor({
  sym,
  index,
  count,
  masterdataFields,
  sourceFields,
  onUpdate,
  onMove,
  onRemove,
}: {
  sym: LocalSymbol;
  index: number;
  count: number;
  masterdataFields: BuilderField[];
  sourceFields: PlanSourceField[];
  onUpdate: (localKey: string, patch: Partial<LocalSymbol>) => void;
  onMove: (localKey: string, dir: -1 | 1) => void;
  onRemove: (localKey: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const alwaysVisible = sym.rules.some((r) => r.rule_type === "alwaysVisible");
  const visibleIfRules = sym.rules.filter((r) => r.rule_type === "visibleIf");
  const detectedTokens = extractPlaceholderTokens(sym.svg_markup);

  function setRules(rules: SymbolRule[]) {
    onUpdate(sym.localKey, { rules });
  }

  function setBindings(bindings: PlaceholderBinding[]) {
    onUpdate(sym.localKey, { placeholderBindings: bindings });
  }

  async function handleSvgFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      onUpdate(sym.localKey, { svg_markup: text, png_preview_base64: "" });
    } catch {
      // Datei konnte nicht gelesen werden – still ignorieren.
    }
  }

  return (
    <article className="mb-ps-card">
      <div className="mb-ps-card__header">
        <span className="mb-ps-card__index" title={`Symbol ${index + 1}`}>
          {index + 1}
        </span>
        <div className="mb-ps-card__title-field">
          <input
            type="text"
            className="eh-input w-full"
            aria-label="Bezeichnung"
            placeholder="z. B. Hydrant, Rauchmelder …"
            value={sym.title}
            onChange={(e) => onUpdate(sym.localKey, { title: e.target.value })}
          />
        </div>
        <div className="mb-ps-card__actions">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={index === 0}
            onClick={() => onMove(sym.localKey, -1)}
            aria-label="Nach oben"
            title="Nach oben"
          >
            <ChevronUpIcon className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={index === count - 1}
            onClick={() => onMove(sym.localKey, 1)}
            aria-label="Nach unten"
            title="Nach unten"
          >
            <ChevronDownIcon className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="danger"
            onClick={() => onRemove(sym.localKey)}
            aria-label="Symbol entfernen"
            title="Entfernen"
          >
            <TrashIcon className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>

      <div className="mb-ps-card__body">
        <div className="mb-ps-main">
          <section className="mb-ps-panel">
            <h3 className="mb-ps-panel__title">Sichtbarkeit im Planlabel</h3>
            <p className="mb-ps-panel__hint">
              Symbole werden von oben nach unten geprüft. Das erste passende Symbol wird verwendet.
              „Immer sichtbar“ eignet sich für Basissymbole am Ende der Liste.
            </p>
            <Checkbox
              id={`${sym.localKey}-always`}
              label="Immer sichtbar"
              checked={alwaysVisible}
              onChange={(e) => setRules(e.target.checked ? [{ rule_type: "alwaysVisible" }] : [])}
            />

            {!alwaysVisible ? (
              <VisibilityRulesEditor rules={visibleIfRules} sources={sourceFields} onChange={setRules} />
            ) : null}
          </section>

          <section className="mb-ps-panel">
            <h3 className="mb-ps-panel__title">SVG-Platzhalter</h3>
            <p className="mb-ps-panel__hint">
              Im SVG Text wie <code className="rounded bg-fill-quaternary px-1 py-0.5 font-mono text-[0.7rem]">{"{{Name}}"}</code>{" "}
              einfügen und unten Stammdaten oder Objektfelder zuordnen.
            </p>

            <p className="text-xs text-label-secondary">Automatisch erkannt:</p>
            <div className="mb-ps-chips mt-1">
              {detectedTokens.length ? (
                detectedTokens.map((token) => (
                  <span key={token} className="mb-ps-chip">
                    {token}
                  </span>
                ))
              ) : (
                <span className="text-xs text-label-tertiary">Keine Platzhalter im Markup</span>
              )}
            </div>

            <div className="mb-ps-stack mt-3">
              {sym.placeholderBindings.map((binding, bindingIndex) => (
                <div key={bindingIndex} className="mb-ps-binding-row">
                  <SelectInput
                    label="Platzhalter"
                    value={binding.placeholder}
                    onChange={(e) =>
                      setBindings(
                        sym.placeholderBindings.map((b, j) =>
                          j === bindingIndex ? { ...b, placeholder: e.target.value } : b,
                        ),
                      )
                    }
                  >
                    <option value="">
                      {detectedTokens.length ? "— wählen —" : "Keine im SVG erkannt"}
                    </option>
                    {binding.placeholder && !detectedTokens.includes(binding.placeholder) ? (
                      <option value={binding.placeholder}>{binding.placeholder} (nicht im SVG)</option>
                    ) : null}
                    {detectedTokens.map((token) => (
                      <option key={token} value={token}>
                        {token}
                      </option>
                    ))}
                  </SelectInput>
                  <SelectInput
                    label="Quelle"
                    value={binding.source_field_key}
                    onChange={(e) =>
                      setBindings(
                        sym.placeholderBindings.map((b, j) =>
                          j === bindingIndex ? { ...b, source_field_key: e.target.value } : b,
                        ),
                      )
                    }
                  >
                    <option value="">— wählen —</option>
                    <optgroup label="Objekt">
                      {PLAN_LABEL_BUILTIN_SOURCES.map((src) => (
                        <option key={src.key} value={src.key}>
                          {src.label}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Stammdaten">
                      {masterdataFields.map((f) => (
                        <option key={f.uid} value={f.field_key}>
                          {f.label || f.field_key}
                        </option>
                      ))}
                    </optgroup>
                  </SelectInput>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setBindings(sym.placeholderBindings.filter((_, j) => j !== bindingIndex))}
                  >
                    Entfernen
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() =>
                  setBindings([
                    ...sym.placeholderBindings,
                    { placeholder: detectedTokens[0] || "", source_field_key: "" },
                  ])
                }
              >
                Zuordnung hinzufügen
              </Button>
            </div>
          </section>
        </div>

        <aside className="mb-ps-aside">
          <PlanSymbolPreview svgMarkup={sym.svg_markup} pngBase64={sym.png_preview_base64} title={sym.title} />

          <div className="mb-ps-upload">
            <span className="text-xs font-medium text-label-secondary">SVG-Datei</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".svg,image/svg+xml"
              className="sr-only"
              onChange={(e) => void handleSvgFile(e)}
            />
            <Button type="button" size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()}>
              SVG hochladen
            </Button>
          </div>

          <div className="mb-ps-markup">
            <TextareaInput
              label="SVG-Markup"
              hint="Änderungen werden sofort in der Vorschau angezeigt."
              value={sym.svg_markup}
              spellCheck={false}
              inputClassName="mb-ps-markup__textarea"
              onChange={(e) => onUpdate(sym.localKey, { svg_markup: e.target.value })}
            />
          </div>
        </aside>
      </div>
    </article>
  );
}

export function VisibilityRulesEditor({
  rules,
  sources,
  onChange,
}: {
  rules: SymbolRule[];
  sources: PlanSourceField[];
  onChange: (rules: SymbolRule[]) => void;
}) {
  function patchRule(ruleIndex: number, patch: Partial<SymbolRule>) {
    onChange(rules.map((rule, index) => (index === ruleIndex ? { ...rule, ...patch } : rule)));
  }

  const sentence = formatPlanVisibilitySentence(
    rules.map((rule) => ({
      join: rule.join,
      text: planRuleClauseText(
        rule,
        sources.find((source) => source.key === rule.source_field_key) ?? null,
      ),
    })),
  );

  return (
    <div className="mb-ps-stack mt-3">
      <p className="mb-ps-panel__hint">
        „und“ verlangt, dass die Bedingungen zusammen gelten. „oder“ startet eine Alternative. Beispiel:
        Türqualität ist keine, oder Fluchttür ist ja und Panikschloss ist ja.
      </p>
      {rules.map((rule, ruleIndex) => {
        const source = sources.find((entry) => entry.key === rule.source_field_key) ?? null;
        const join: PlanRuleJoin = rule.join === "or" ? "or" : "and";
        return (
          <Fragment key={ruleIndex}>
            {ruleIndex > 0 ? (
              <div className="mb-ps-join">
                <SegmentedControl
                  name={`Verknüpfung mit Bedingung ${ruleIndex}`}
                  value={join}
                  onChange={(next) => patchRule(ruleIndex, { join: next })}
                  options={[
                    { value: "and", label: "und" },
                    { value: "or", label: "oder" },
                  ]}
                />
              </div>
            ) : null}
            <div className="mb-ps-rule-row">
              <SelectInput
                label="Stammdatenfeld"
                value={rule.source_field_key || ""}
                onChange={(e) =>
                  patchRule(ruleIndex, {
                    rule_type: "visibleIf",
                    source_scope: "masterdata",
                    source_field_key: e.target.value,
                    value: "",
                  })
                }
              >
                <option value="">— wählen —</option>
                {sources.map((field) => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                  </option>
                ))}
              </SelectInput>
              <SelectInput
                label="Operator"
                value={normalizeOperator(rule.operator)}
                onChange={(e) =>
                  patchRule(ruleIndex, {
                    operator: e.target.value,
                    value: coerceRuleValueForOperator(e.target.value, rule.value),
                  })
                }
              >
                {operatorChoicesForSourceField(source, rule.operator).map((op) => (
                  <option key={op} value={op}>
                    {BEHAVIOR_OPERATOR_LABELS[op] || op}
                  </option>
                ))}
              </SelectInput>
              <PlanRuleAnswer
                rule={rule}
                source={source}
                onChange={(value) => patchRule(ruleIndex, { value })}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onChange(rules.filter((_, index) => index !== ruleIndex))}
              >
                Entfernen
              </Button>
            </div>
          </Fragment>
        );
      })}
      {sentence ? <p className="mb-ps-logic">Sichtbar, wenn {sentence}.</p> : null}
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() =>
          onChange([
            ...rules,
            {
              rule_type: "visibleIf",
              source_scope: "masterdata",
              source_field_key: "",
              operator: "eq",
              value: "",
              join: "and",
            },
          ])
        }
      >
        Sichtbarkeitsregel hinzufügen
      </Button>
    </div>
  );
}

function PlanRuleAnswer({
  rule,
  source,
  onChange,
}: {
  rule: SymbolRule;
  source: PlanSourceField | null;
  onChange: (value: unknown) => void;
}) {
  if (isBooleanSourceField(source)) {
    const selected = planBooleanSelection(rule.value);
    const custom =
      selected !== "true" && selected !== "false" && selected !== "" ? selected : "";
    return (
      <SelectInput
        label="Antwort"
        value={selected}
        onChange={(e) => {
          const next = e.target.value;
          if (next === "true") onChange(true);
          else if (next === "false") onChange(false);
          else if (next === "") onChange("");
          else onChange(next);
        }}
      >
        <option value="">Bitte wählen …</option>
        <option value="true">{source?.trueLabel || "Ja"}</option>
        <option value="false">{source?.falseLabel || "Nein"}</option>
        {custom ? <option value={custom}>{custom} (aktuell)</option> : null}
      </SelectInput>
    );
  }

  const hasAnswers = (source?.valueOptions.length ?? 0) > 0 || ruleHasAnswer(rule.value);
  if (source && isOptionSourceField(source) && hasAnswers) {
    return (
      <RuleValueInput
        label="Antwort"
        value={rule.value}
        sourceField={source}
        operator={normalizeOperator(rule.operator)}
        onChange={onChange}
      />
    );
  }

  return (
    <RuleValueInput
      label="Antwort"
      value={rule.value}
      sourceField={null}
      operator={normalizeOperator(rule.operator)}
      onChange={onChange}
    />
  );
}

function ruleHasAnswer(value: unknown): boolean {
  if (Array.isArray(value)) return value.some((entry) => String(entry ?? "").trim());
  return String(value ?? "").trim() !== "";
}
